import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { resolveRecordingPlayback } from '../lib/recording-playback';

export interface Track {
  id: string;
  url: string;
  title: string;
  subtitle?: string;
  phone?: string;
}

interface AudioControls {
  track: Track | null;
  playbackUrl: string | null;
  playing: boolean;
  rate: number;
  play: (track: Track) => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  setRate: (rate: number) => void;
  stop: () => void;
}

const AudioContext = createContext<AudioControls | null>(null);

// Playback position changes several times a second. It lives outside React
// state so only the components that show it re-render, not every table row.
const timeStore = {
  current: 0,
  duration: 0,
  listeners: new Set<() => void>(),
  set(current: number, duration: number) {
    this.current = current;
    this.duration = duration;
    this.listeners.forEach((l) => l());
  },
  subscribe(listener: () => void) {
    timeStore.listeners.add(listener);
    return () => timeStore.listeners.delete(listener);
  },
};

/**
 * Some phones save calls as AMR or 3GP, which browsers can't play. The file is fine, so
 * offer it as a download instead of saying it may be deleted.
 */
function unplayableFormat(url: string) {
  toast.error('This recording’s audio format can’t play in the browser.', {
    description: 'Some phones save calls as AMR or 3GP. Download it to play it on your computer or phone.',
    action: { label: 'Download', onClick: () => window.open(url, '_blank', 'noopener') },
    duration: 10_000,
  });
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackGeneration = useRef(0);
  const [track, setTrack] = useState<Track | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(1);

  if (!audioRef.current && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'metadata';
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () =>
      timeStore.set(audio.currentTime, Number.isFinite(audio.duration) ? audio.duration : 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      timeStore.set(0, timeStore.duration);
    };
    const onError = () => {
      if (!audio.src) return;
      setPlaying(false);
      if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) return unplayableFormat(audio.src);
      toast.error('This recording couldn’t be played. It may have been deleted or is still uploading.');
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('durationchange', onTime);
    audio.addEventListener('loadedmetadata', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('durationchange', onTime);
      audio.removeEventListener('loadedmetadata', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, []);

  const play = useCallback(
    (next: Track) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (track?.id === next.id && audio.getAttribute('src')) {
        if (audio.paused) audio.play().catch(() => undefined);
        else audio.pause();
        return;
      }
      setTrack(next);
      setPlaybackUrl(null);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      const generation=++playbackGeneration.current;
      timeStore.set(0, 0);
      resolveRecordingPlayback(next.url).then((url)=>{
        if(generation!==playbackGeneration.current)return;
        setPlaybackUrl(url);
        audio.src=url;audio.playbackRate=rate;
        return audio.play();
      }).catch((err) => {
        if(generation!==playbackGeneration.current||err?.name==='AbortError')return;
        if(err?.name==='NotSupportedError'&&audio.src)return unplayableFormat(audio.src);
        toast.error(err?.message||'Couldn’t start playback. Tap play to try again.');
      });
    },
    [track?.id, rate],
  );

  const value = useMemo<AudioControls>(
    () => ({
      track,
      playbackUrl,
      playing,
      rate,
      play,
      toggle: () => {
        const audio = audioRef.current;
        if (!audio || !track) return;
        if (audio.paused) audio.play().catch(() => undefined);
        else audio.pause();
      },
      seek: (seconds) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = Math.max(0, seconds);
        timeStore.set(audio.currentTime, timeStore.duration);
      },
      setRate: (r) => {
        setRateState(r);
        if (audioRef.current) audioRef.current.playbackRate = r;
      },
      stop: () => {
        ++playbackGeneration.current;
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
        }
        setTrack(null);
        setPlaybackUrl(null);
        setPlaying(false);
        timeStore.set(0, 0);
      },
    }),
    [track, playbackUrl, playing, rate, play],
  );

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
}

export function useAudio() {
  const ctx = useContext(AudioContext);
  if (!ctx) throw new Error('useAudio must be used inside AudioProvider');
  return ctx;
}

export function useAudioTime() {
  const current = useSyncExternalStore(timeStore.subscribe, () => timeStore.current);
  const duration = useSyncExternalStore(timeStore.subscribe, () => timeStore.duration);
  return { current, duration };
}
